/**
 * Poker Engine - Core poker logic shared across all variants
 * Handles deck management, hand evaluation, and comparison
 */

import { SUITS, RANKS, THREE_CARD_HANDS, HOLDEM_HANDS } from './pokerConstants.js';

/**
 * Create a standard 52-card deck
 */
export function createDeck() {
  const deck = [];
  let id = 0;
  
  Object.keys(SUITS).forEach(suit => {
    Object.keys(RANKS).forEach(rankKey => {
      const rank = parseInt(rankKey);
      deck.push({
        id: id++,
        suit,
        rank,
        value: rank,
        display: RANKS[rank].display,
        symbol: SUITS[suit].symbol,
        color: SUITS[suit].color
      });
    });
  });
  
  return deck;
}

/**
 * Fisher-Yates shuffle
 */
export function shuffleDeck(deck) {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Deal cards from the deck
 */
export function dealCards(deck, count) {
  return {
    dealt: deck.slice(0, count),
    remaining: deck.slice(count)
  };
}

/**
 * Sort cards by rank (highest first)
 */
export function sortByRank(cards) {
  return [...cards].sort((a, b) => b.rank - a.rank);
}

/**
 * Sort cards by suit then rank
 */
export function sortBySuit(cards) {
  return [...cards].sort((a, b) => {
    const suitDiff = SUITS[b.suit].order - SUITS[a.suit].order;
    if (suitDiff !== 0) return suitDiff;
    return b.rank - a.rank;
  });
}

// ============================================
// THREE CARD STUD HAND EVALUATION
// ============================================

/**
 * Check if cards form a flush (all same suit)
 */
function isFlush(cards) {
  return cards.every(c => c.suit === cards[0].suit);
}

/**
 * Check if cards form a straight (consecutive ranks)
 * Handles A-2-3 low straight
 */
function isStraight(cards) {
  const sorted = sortByRank(cards);
  const ranks = sorted.map(c => c.rank);
  
  // Check normal straight
  const isConsecutive = ranks[0] - ranks[1] === 1 && ranks[1] - ranks[2] === 1;
  
  // Check A-2-3 (wheel)
  const isWheel = ranks[0] === 14 && ranks[1] === 3 && ranks[2] === 2;
  
  return isConsecutive || isWheel;
}

/**
 * Check for three of a kind
 */
function isThreeOfAKind(cards) {
  return cards[0].rank === cards[1].rank && cards[1].rank === cards[2].rank;
}

/**
 * Check for a pair and return the pair rank and kicker
 */
function getPairInfo(cards) {
  const sorted = sortByRank(cards);
  
  if (sorted[0].rank === sorted[1].rank) {
    return { hasPair: true, pairRank: sorted[0].rank, kicker: sorted[2].rank };
  }
  if (sorted[1].rank === sorted[2].rank) {
    return { hasPair: true, pairRank: sorted[1].rank, kicker: sorted[0].rank };
  }
  
  return { hasPair: false };
}

/**
 * Evaluate a 3-card poker hand
 * Returns: { hand: THREE_CARD_HANDS.*, highCards: [ranks], score: number }
 */
export function evaluateThreeCardHand(cards) {
  if (cards.length !== 3) {
    throw new Error('Three Card Stud requires exactly 3 cards');
  }
  
  const sorted = sortByRank(cards);
  const flush = isFlush(cards);
  const straight = isStraight(cards);
  const trips = isThreeOfAKind(cards);
  const pairInfo = getPairInfo(cards);
  
  // Get high cards for tiebreakers (handle wheel straight specially)
  let highCards = sorted.map(c => c.rank);
  if (straight && highCards[0] === 14 && highCards[2] === 2) {
    // A-2-3 wheel - Ace plays low
    highCards = [3, 2, 1]; // 1 represents low ace
  }
  
  // Determine hand rank
  if (straight && flush) {
    return {
      hand: THREE_CARD_HANDS.STRAIGHT_FLUSH,
      highCards,
      score: 6000000 + highCards[0] * 10000 + highCards[1] * 100 + highCards[2]
    };
  }
  
  if (trips) {
    return {
      hand: THREE_CARD_HANDS.THREE_OF_A_KIND,
      highCards,
      score: 5000000 + highCards[0] * 10000
    };
  }
  
  if (straight) {
    return {
      hand: THREE_CARD_HANDS.STRAIGHT,
      highCards,
      score: 4000000 + highCards[0] * 10000 + highCards[1] * 100 + highCards[2]
    };
  }
  
  if (flush) {
    return {
      hand: THREE_CARD_HANDS.FLUSH,
      highCards,
      score: 3000000 + highCards[0] * 10000 + highCards[1] * 100 + highCards[2]
    };
  }
  
  if (pairInfo.hasPair) {
    return {
      hand: THREE_CARD_HANDS.PAIR,
      highCards: [pairInfo.pairRank, pairInfo.pairRank, pairInfo.kicker],
      score: 2000000 + pairInfo.pairRank * 10000 + pairInfo.kicker
    };
  }
  
  // High card
  return {
    hand: THREE_CARD_HANDS.HIGH_CARD,
    highCards,
    score: 1000000 + highCards[0] * 10000 + highCards[1] * 100 + highCards[2]
  };
}

/**
 * Check if dealer qualifies in Three Card Stud (Queen high or better)
 */
export function dealerQualifies(cards) {
  const sorted = sortByRank(cards);
  return sorted[0].rank >= 12; // Queen = 12
}

/**
 * Compare two Three Card Stud hands
 * Returns: 1 if hand1 wins, -1 if hand2 wins, 0 if tie
 */
export function compareThreeCardHands(hand1, hand2) {
  const eval1 = evaluateThreeCardHand(hand1);
  const eval2 = evaluateThreeCardHand(hand2);
  
  if (eval1.score > eval2.score) return 1;
  if (eval1.score < eval2.score) return -1;
  return 0;
}

// ============================================
// TEXAS HOLD'EM HAND EVALUATION (5-7 cards)
// ============================================

/**
 * Get all 5-card combinations from cards
 */
function getCombinations(cards, k) {
  const result = [];
  
  function combine(start, combo) {
    if (combo.length === k) {
      result.push([...combo]);
      return;
    }
    for (let i = start; i < cards.length; i++) {
      combo.push(cards[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  }
  
  combine(0, []);
  return result;
}

/**
 * Evaluate a 5-card poker hand
 */
export function evaluateFiveCardHand(cards) {
  if (cards.length !== 5) {
    throw new Error('Requires exactly 5 cards');
  }
  
  const sorted = sortByRank(cards);
  const ranks = sorted.map(c => c.rank);
  const suits = sorted.map(c => c.suit);
  
  const isFlush5 = suits.every(s => s === suits[0]);
  
  // Check straight
  let isStraight5 = true;
  for (let i = 0; i < 4; i++) {
    if (ranks[i] - ranks[i + 1] !== 1) {
      isStraight5 = false;
      break;
    }
  }
  // Check wheel (A-2-3-4-5)
  const isWheel5 = ranks[0] === 14 && ranks[1] === 5 && ranks[2] === 4 && ranks[3] === 3 && ranks[4] === 2;
  if (isWheel5) isStraight5 = true;
  
  // Count rank occurrences
  const rankCounts = {};
  ranks.forEach(r => { rankCounts[r] = (rankCounts[r] || 0) + 1; });
  const counts = Object.values(rankCounts).sort((a, b) => b - a);
  
  // Determine hand
  const isRoyalFlush = isFlush5 && isStraight5 && ranks[0] === 14 && ranks[4] === 10;
  const isStraightFlush5 = isFlush5 && isStraight5;
  const isFourOfAKind = counts[0] === 4;
  const isFullHouse = counts[0] === 3 && counts[1] === 2;
  const isThreeOfAKind5 = counts[0] === 3 && counts[1] === 1;
  const isTwoPair = counts[0] === 2 && counts[1] === 2;
  const isPair5 = counts[0] === 2 && counts[1] === 1;
  
  let hand, score;
  
  if (isRoyalFlush) {
    hand = HOLDEM_HANDS.ROYAL_FLUSH;
    score = 10000000;
  } else if (isStraightFlush5) {
    hand = HOLDEM_HANDS.STRAIGHT_FLUSH;
    score = 9000000 + (isWheel5 ? 5 : ranks[0]);
  } else if (isFourOfAKind) {
    const quadRank = Object.keys(rankCounts).find(r => rankCounts[r] === 4);
    const kicker = Object.keys(rankCounts).find(r => rankCounts[r] === 1);
    hand = HOLDEM_HANDS.FOUR_OF_A_KIND;
    score = 8000000 + parseInt(quadRank) * 100 + parseInt(kicker);
  } else if (isFullHouse) {
    const tripRank = Object.keys(rankCounts).find(r => rankCounts[r] === 3);
    const pairRank = Object.keys(rankCounts).find(r => rankCounts[r] === 2);
    hand = HOLDEM_HANDS.FULL_HOUSE;
    score = 7000000 + parseInt(tripRank) * 100 + parseInt(pairRank);
  } else if (isFlush5) {
    hand = HOLDEM_HANDS.FLUSH;
    score = 6000000 + ranks[0] * 10000 + ranks[1] * 1000 + ranks[2] * 100 + ranks[3] * 10 + ranks[4];
  } else if (isStraight5) {
    hand = HOLDEM_HANDS.STRAIGHT;
    score = 5000000 + (isWheel5 ? 5 : ranks[0]);
  } else if (isThreeOfAKind5) {
    const tripRank = Object.keys(rankCounts).find(r => rankCounts[r] === 3);
    const kickers = Object.keys(rankCounts).filter(r => rankCounts[r] === 1).sort((a, b) => b - a);
    hand = HOLDEM_HANDS.THREE_OF_A_KIND;
    score = 4000000 + parseInt(tripRank) * 10000 + parseInt(kickers[0]) * 100 + parseInt(kickers[1]);
  } else if (isTwoPair) {
    const pairs = Object.keys(rankCounts).filter(r => rankCounts[r] === 2).sort((a, b) => b - a);
    const kicker = Object.keys(rankCounts).find(r => rankCounts[r] === 1);
    hand = HOLDEM_HANDS.TWO_PAIR;
    score = 3000000 + parseInt(pairs[0]) * 10000 + parseInt(pairs[1]) * 100 + parseInt(kicker);
  } else if (isPair5) {
    const pairRank = Object.keys(rankCounts).find(r => rankCounts[r] === 2);
    const kickers = Object.keys(rankCounts).filter(r => rankCounts[r] === 1).sort((a, b) => b - a);
    hand = HOLDEM_HANDS.PAIR;
    score = 2000000 + parseInt(pairRank) * 10000 + parseInt(kickers[0]) * 100 + parseInt(kickers[1]) * 10 + parseInt(kickers[2]);
  } else {
    hand = HOLDEM_HANDS.HIGH_CARD;
    score = 1000000 + ranks[0] * 10000 + ranks[1] * 1000 + ranks[2] * 100 + ranks[3] * 10 + ranks[4];
  }
  
  return { hand, highCards: ranks, score };
}

/**
 * Find the best 5-card hand from 7 cards (for Texas Hold'em)
 */
export function bestFiveCardHand(cards) {
  if (cards.length < 5) {
    throw new Error('Need at least 5 cards');
  }
  
  if (cards.length === 5) {
    return { cards, evaluation: evaluateFiveCardHand(cards) };
  }
  
  const combinations = getCombinations(cards, 5);
  let best = null;
  let bestScore = -1;
  
  combinations.forEach(combo => {
    const evaluation = evaluateFiveCardHand(combo);
    if (evaluation.score > bestScore) {
      bestScore = evaluation.score;
      best = { cards: combo, evaluation };
    }
  });
  
  return best;
}

// ============================================
// SOUND EFFECTS
// ============================================

export const PokerSounds = {
  audioContext: null,
  
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  },
  
  // Chip click sound
  playChip() {
    try {
      const ctx = this.init();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(2000, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.03);
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) { /* Audio not supported */ }
  },
  
  // Card deal sound
  playDeal() {
    try {
      const ctx = this.init();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.04);
      
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.08);
    } catch (e) { /* Audio not supported */ }
  },
  
  // Card flip/reveal sound
  playFlip() {
    try {
      const ctx = this.init();
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      
      for (let i = 0; i < buffer.length; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / buffer.length) * 0.3;
      }
      
      noise.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 3000;
      
      noise.connect(filter);
      filter.connect(ctx.destination);
      noise.start(ctx.currentTime);
    } catch (e) { /* Audio not supported */ }
  },
  
  // Win sound - ascending arpeggio
  playWin() {
    try {
      const ctx = this.init();
      const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
      
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.08);
        gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + i * 0.08 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.4);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.08);
        osc.stop(ctx.currentTime + i * 0.08 + 0.5);
      });
    } catch (e) { /* Audio not supported */ }
  },
  
  // Lose sound - descending
  playLose() {
    try {
      const ctx = this.init();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(400, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.3);
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) { /* Audio not supported */ }
  },
  
  // Shuffle sound
  playShuffle() {
    try {
      const ctx = this.init();
      
      for (let i = 0; i < 8; i++) {
        const noise = ctx.createBufferSource();
        const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.03, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let j = 0; j < buffer.length; j++) {
          data[j] = (Math.random() * 2 - 1) * 0.2;
        }
        
        noise.buffer = buffer;
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 2000 + Math.random() * 1000;
        
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.1, ctx.currentTime + i * 0.04);
        
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        noise.start(ctx.currentTime + i * 0.04);
      }
    } catch (e) { /* Audio not supported */ }
  }
};
